import { customColors } from '@apollo/orbit';
import classnames from 'classnames';
import { last } from 'lodash';
import React, { Fragment, useMemo } from 'react';

import { assertUnreachable } from 'src/lib/assertUnreachable';

const { border } = customColors;

export const CHAR_WIDTH = 8;
export const CHAR_HEIGHT = 12;
function getTickValues({
  side,
  y,
  x,
  tickOffset,
}: {
  side: 'right' | 'bottom' | 'left';
  y: number;
  x: number;
  tickOffset: number;
}) {
  if (side === 'right') {
    return {
      positionX: x,
      tickY: tickOffset + y,
      tickProps: { x1: x + strokeWidth, x2: x + 3 + strokeWidth },
      textProps: (i: number) => ({ dy: '0.32em', x: 6, y: 10 * i }),
    };
  }
  if (side === 'left') {
    return {
      positionX: x,
      tickY: tickOffset + y,
      tickProps: { x1: x - strokeWidth, x2: x - 3 - strokeWidth },
      textProps: (i: number) => ({
        dy: '0.32em',
        x: -6,
        y: 10 * i,
        textAnchor: 'end',
      }),
    };
  }
  if (side === 'bottom') {
    return {
      positionY: y,
      tickX: tickOffset + x,
      tickProps: { y1: y + strokeWidth, y2: y + 3 + strokeWidth },
      textProps: (i: number) => ({
        dy: '0.71em',
        y: 6 + 10 * i,
        textAnchor: 'middle',
      }),
    };
  }
  assertUnreachable(side);
}
export function useFilteredTicks<Value>({
  tickValues,
  scale,
  defaultValue,
  side = 'bottom',
}: {
  tickValues: Array<{ labels: string[]; value: Value | null }>;
  scale: (value: Value) => number;
  defaultValue: Value;
  side?: 'right' | 'bottom';
}) {
  return useMemo(() => {
    const ranges: Array<[number, number]> = [];
    return tickValues
      .map((tick) => {
        const tickLength =
          side === 'bottom'
            ? CHAR_WIDTH * Math.max(...tick.labels.map((l) => l.length))
            : side === 'right'
              ? CHAR_HEIGHT * tick.labels.length
              : assertUnreachable(side);
        const tickPosition = scale(tick.value ?? defaultValue);
        const labelStart = tickPosition - tickLength / 2;
        const labelEnd = tickPosition + tickLength / 2;
        return { ...tick, labelEnd, labelStart };
      })
      .filter(
        (
          tick,
        ): tick is Omit<typeof tick, 'value'> & {
          value: Exclude<typeof tick.value, null>;
        } => {
          if (tick.value === null) return false;
          const conflictRange = ranges.find(
            ([rangegStart, rangeEnd]) =>
              tick.labelEnd >= rangegStart && tick.labelStart <= rangeEnd,
          );
          if (conflictRange) return false;
          ranges.push([tick.labelStart, tick.labelEnd]);
          return true;
        },
      );
  }, [defaultValue, scale, side, tickValues]);
}

export function useHiddenTickIndexes<Value>({
  ticks,
  cursorTick,
  scale,
  side,
}: {
  ticks: ReturnType<typeof useFilteredTicks>;
  cursorTick: { labels: string[]; value: Value } | undefined;
  scale: (value: Value) => number;
  side: 'right' | 'bottom';
}) {
  return useMemo(() => {
    if (!cursorTick) return [];
    const cursorLength =
      side === 'bottom'
        ? CHAR_WIDTH * Math.max(...cursorTick.labels.map((l) => l.length))
        : side === 'right'
          ? CHAR_HEIGHT * (cursorTick.labels.length + 2)
          : assertUnreachable(side);
    const cursorPosition = scale(cursorTick.value);
    const cursorStart = cursorPosition - cursorLength / 2;
    const cursorEnd = cursorPosition + cursorLength / 2;
    return ticks
      .map((tick, index) => ({ index, tick }))
      .filter(
        ({ tick }) =>
          tick.labelEnd >= cursorStart && tick.labelStart <= cursorEnd,
      )
      .map(({ index }) => index + 1);
  }, [cursorTick, scale, side, ticks]);
}

const strokeWidth = 1;
export function Axis<Value>({
  x = 0,
  y = 0,
  hideDomain = false,
  withLines = false,
  scale,
  tickValues,
  cursorTick,
  hiddenTicks,
  side,
  cursorContrastMode = 'default',
}: {
  scale: ((value: Value) => number) & { range(): number[] };
  x?: number;
  y?: number;
  hideDomain?: boolean;
  tickValues: Array<{ value: Value; labels: string[] }>;
  cursorTick?: { value: Value; labels: string[] };
  hiddenTicks?: number[];
  side: 'right' | 'bottom' | 'left';
  withLines?: boolean;
  cursorContrastMode?: 'default' | 'high';
}) {
  const length = last(scale.range()) ?? 0;
  const { domainX2, domainY2 } =
    side === 'right' || side === 'left'
      ? { domainY2: length, domainX2: strokeWidth / 2 }
      : side === 'bottom'
        ? { domainY2: strokeWidth / 2, domainX2: length }
        : assertUnreachable(side);

  return (
    <g className="text-sm">
      {!hideDomain && (
        <line
          x2={domainX2}
          y2={domainY2}
          stroke={border.primary}
          strokeWidth={strokeWidth}
          transform={`translate(${x}, ${y})`}
        />
      )}
      {[...tickValues, ...(cursorTick ? [cursorTick] : [])].map(
        (tick, tickIndex) => {
          const visibility = hiddenTicks?.includes(tickIndex + 1)
            ? 'hidden'
            : 'visible';
          const tickOffset = scale(tick.value);
          const lineMargin = withLines ? 10 : 0;
          const {
            positionX = 0,
            positionY = 0,
            tickProps,
            textProps,
            tickX,
            tickY,
          } = getTickValues({ side, x, y, tickOffset });
          return (
            <Fragment
              key={
                tick === cursorTick
                  ? 'cursor'
                  : `${tick.value}${tick.labels.join()}`
              }
            >
              {withLines && (
                <line
                  visibility={visibility}
                  stroke={border.primary}
                  transform={`translate(${tickX ?? 0}, ${tickY ?? 0})`}
                  x2={positionX}
                  y2={positionY}
                />
              )}
              <g
                visibility={visibility}
                transform={`translate(${tickX ?? lineMargin}, ${
                  tickY ?? lineMargin
                })`}
              >
                <line stroke={border.primary} {...tickProps} />
                {tick.labels.map((label, i) => {
                  const isCursorTick = tick === cursorTick;
                  const shouldHighlight =
                    isCursorTick && cursorContrastMode === 'high';

                  return (
                    <g
                      key={label}
                      transform={`translate(${positionX}, ${positionY})`}
                    >
                      {shouldHighlight && (
                        <rect
                          x="-35"
                          y="4"
                          width="70"
                          height="24"
                          rx="4"
                          fill={customColors.bg.black}
                          stroke={border.primary}
                          className="text-sm"
                        />
                      )}
                      <text
                        {...(shouldHighlight
                          ? { dy: '1.45em', textAnchor: 'middle' }
                          : textProps(i))}
                        fontWeight={isCursorTick ? '600' : undefined}
                        fill={
                          shouldHighlight
                            ? 'white'
                            : i === 0
                              ? customColors.text.primary
                              : customColors.text.secondary
                        }
                        className={classnames({ 'text-xs': i !== 0 })}
                      >
                        {label}
                      </text>
                    </g>
                  );
                })}
              </g>
            </Fragment>
          );
        },
      )}
    </g>
  );
}
